import {
  useEffect, useRef, useState 
} from 'react';
import {
  Download, Copy, Check, Printer 
} from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/classnames';

interface QRCodeDisplayProps {
  readonly value: string;
  readonly size?: number;
  readonly title?: string;
  readonly subtitle?: string;
  readonly className?: string;
  readonly showActions?: boolean;
}

/**
 * Generates a QR code using the Canvas API.
 * This is a simple implementation that creates QR codes without external dependencies.
 */
function generateQRCodeDataURL(text: string, size: number): string {
  // Use a simple QR code generation via Google Charts API as fallback
  // In production, you'd use a library like 'qrcode' for offline generation
  const encodedText = encodeURIComponent(text);
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedText}&format=png&margin=10`;
}

export function QRCodeDisplay({
  value,
  size = 200,
  title,
  subtitle,
  className,
  showActions = true,
}: QRCodeDisplayProps) {
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQrDataUrl(generateQRCodeDataURL(value, size));
  }, [value, size]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers using selection API
      const textArea = document.createElement('textarea');
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      const selection = document.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `qr-code-${title?.replace(/\s+/g, '-').toLowerCase() ?? 'download'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `<!DOCTYPE html>
<html>
<head>
<title>QR Code - ${title ?? 'Print'}</title>
<style>
body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
}
.container {
  text-align: center;
  padding: 40px;
}
h1 {
  font-size: 24px;
  margin: 0 0 8px 0;
}
p {
  font-size: 14px;
  color: #666;
  margin: 0 0 24px 0;
}
img {
  max-width: 300px;
  height: auto;
}
.url {
  font-size: 12px;
  color: #999;
  margin-top: 16px;
  word-break: break-all;
  max-width: 300px;
}
</style>
</head>
<body>
<div class="container">
${title ? `<h1>${title}</h1>` : ''}
${subtitle ? `<p>${subtitle}</p>` : ''}
<img src="${qrDataUrl}" alt="QR Code" />
<div class="url">${value}</div>
</div>
<script>
window.onload = function() {
  window.print();
  window.onafterprint = function() {
    window.close();
  };
};
</script>
</body>
</html>`;
    const parser = new DOMParser();
    const doc = parser.parseFromString(printContent, 'text/html');
    printWindow.document.replaceChild(
      printWindow.document.importNode(doc.documentElement, true),
      printWindow.document.documentElement
    );
  };

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div
        ref={printRef}
        className="bg-white p-4 rounded-lg border shadow-sm"
      >
        {title && (
          <h3 className="text-lg font-semibold text-center mb-1">{title}</h3>
        )}
        {subtitle && (
          <p className="text-sm text-gray-500 text-center mb-4">{subtitle}</p>
        )}
        
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt={`QR Code for ${value}`}
            width={size}
            height={size}
            className="mx-auto"
          />
        ) : (
          <div
            className="bg-gray-100 animate-pulse mx-auto"
            style={{
              width: size,
              height: size 
            }}
          />
        )}

        <p className="text-xs text-gray-400 text-center mt-3 max-w-[200px] break-all">
          {value}
        </p>
      </div>

      {showActions && (
        <div className="flex gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-1" />
                Copied
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-1" />
                Copy URL
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
          >
            <Download className="w-4 h-4 mr-1" />
            Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
          >
            <Printer className="w-4 h-4 mr-1" />
            Print
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Generate the receiving URL for a PO number.
 */
export function getReceivingQRUrl(
  poNumber: string,
  baseUrl = 'https://app.galleyos.com'
): string {
  return `${baseUrl}/receiving/goods/${encodeURIComponent(poNumber)}`;
}
